import { NEWS_FEATURE } from '@propertypal/shared/src/constants/content-hub';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { HomepageTakeover } from '@propertypal/shared/src/types/homepageTakeover';
import { Property } from '@propertypal/shared/src/types/property';
import { StrapiResponse } from '@propertypal/shared/src/types/strapi/generic';
import { ContentHubArticle, ConvertedContentHubArticle } from '@propertypal/shared/src/types/strapi/overrides';
import { SuperFeatureProperty } from '@propertypal/shared/src/types/superFeature';
import { findRichText } from '@propertypal/shared/src/utils/content-hub';
import pick from '@propertypal/shared/src/utils/pick';
import platform from '@propertypal/shared/src/utils/platform';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { GetServerSideProps } from 'next/types';
import React, { FunctionComponent } from 'react';
import HomePage from '../components/home/HomePage';
import generateMetaTags from '../components/layout/MetaTags';
import cachedRequest from '../services/cachedRequest';
import cachedRequestStrapi from '../services/cachedRequest.strapi';
import { getCfIpCountry, requireAuthToken } from '../utils/auth';

const LaunchNotification = dynamic(() => import('../components/home/LaunchNotification'));

interface Props {
  superfeatures: SuperFeatureProperty[];
  recent: Property[];
  popular: Property[];
  takeover: HomepageTakeover | null;
  articles: ConvertedContentHubArticle[] | null;
  loggedIn: boolean;
  cfIpCountry: string | null;
}

const metaData = {
  title: `${platform.title} - Find Property For Sale And Rent In Ireland And Northern Ireland`,
  // eslint-disable-next-line max-len
  description: `Browse Through More Than 60,000 Properties For Sale And Rent In Ireland And Northern Ireland. ${platform.title} Is Your Best Friend In Property.`,
  url: '',
};

const simplifyProperty = (property: Property) => {
  return pick(property, [
    'account',
    'agentLogos',
    'images',
    'path',
    'id',
    'displayAddress',
    'displayAddressLine1',
    'displayAddressLine2',
    'price',
    'epc',
    'ber',
    'addressLine1',
    'town',
    'alternativeCurrencyPrice',
    'listingTime',
    'agents',
    'numBedrooms',
    'numBathrooms',
    'numReceptionRooms',
    'viewsInLast7Days',
    'type',
  ]);
};

export const getServerSideProps: GetServerSideProps = requireAuthToken(async (ctx, store) => {
  const result = await axios(
    {
      method: 'GET',
      url: `${API_URL}/homepage`,
    },
    store.dispatch,
    ctx,
  );

  const platformFilter: Record<string, string> = {
    'filters[$and][2][subCategories][$or][0][platform][$eq]':
      platform.title === 'Propertynews' ? 'Propertynews' : 'PropertyPal',
    'filters[$and][2][subCategories][$or][1][platform][$eq]': 'Both',
    'filters[$and][3][$or][0][platform][$eq]': platform.title === 'Propertynews' ? 'Propertynews' : 'PropertyPal',
    'filters[$and][3][$or][1][platform][$eq]': 'Both',
  };

  if (platform.title === 'PropertyPal') {
    platformFilter['filters[$and][2][subCategories][$or][2][platform][$null]'] = 'true';
    platformFilter['filters[$and][3][$or][2][platform][$null]'] = 'true';
  }

  const news: StrapiResponse<ContentHubArticle[]> | null = NEWS_FEATURE
    ? await cachedRequestStrapi('/articles', 1, {
        populate: 'poster,tabletPoster,mobilePoster,subCategories,content',
        'filters[$and][0][$or][0][hidden][$eq]': 'false',
        'filters[$and][0][$or][1][hidden][$null]': 'true',
        'filters[$and][1][subCategories][articleCategory][$in]': 'news-and-analysis',
        ...platformFilter,
        'filters[$and][4][subCategories][$or][0][hiddenOnHomepage][$eq]': 'false',
        'filters[$and][4][subCategories][$or][1][hiddenOnHomepage][$null]': 'true',
        sort: 'publishedAt:desc',
        'pagination[pageSize]': 3,
      })
    : null;

  const articles =
    news?.data?.map((article) => ({
      ...article,
      attributes: {
        ...article.attributes,
        content: article.attributes.excerpt || findRichText(article.attributes.content),
      },
    })) || null;

  const superfeatures = result.data.superfeatures.sort(() => Math.random() - 0.5);

  // determine property for popular properties to either get popular (pp) or topPicks (pn)
  const property = platform.title === 'Propertynews' ? 'topPicks' : 'popular';

  const popular = result.data?.[property]
    ?.sort(() => {
      // don't randomly sort when top picks
      if (property === 'topPicks') return 0;
      return Math.random() - 0.5;
    })
    ?.map(simplifyProperty);

  const siteMapLinks =
    getCfIpCountry(ctx) === 'IE'
      ? await cachedRequest('/site-map/footer/irl', ctx, 6)
      : await cachedRequest('/site-map/footer/nir', ctx, 6);

  let takeover: Props['takeover'] = null;

  if (result.data.takeover) {
    // simplify listing property from takeover in result
    takeover = {
      ...result.data.takeover,
      // Any changes here should be reflected in the HomepageTakeover["listing"] type
      listing: pick(result.data.takeover.listing, [
        'listingLogos',
        'path',
        'price',
        'showHomeOpeningTime',
        'images',
        'displayAddress',
        'displayAddressLine1',
      ]),
    };
  }

  return {
    props: {
      superfeatures,
      recent: result.data?.recent?.map(simplifyProperty) ?? [],
      popular: popular || null,
      siteMapLinks,
      takeover,
      articles,
    },
  };
});

const Home: FunctionComponent<Props> = (props) => {
  return (
    <>
      <Head>{generateMetaTags(metaData)}</Head>

      <HomePage {...props} />

      <LaunchNotification />
    </>
  );
};

export default Home;
